
import { PEER_CONNECTION_STATUS } from "@gotti/core"
import { ensureWRTCAdapterLoadFinished } from './utils.js';
export enum SocketType {
    UDP,
    TCP
}

export interface PeerConnectionConfig {
    channelName?: string,
    iceServers?: Array<RTCIceServer>,
    socketType?: SocketType,
    timeout?: number,
    retries?: number,
    retryTimeout?: number,
}

const defaultConfig = {
    iceServers: [{
        'urls': [
          "stun:stun.l.google.com:19302",
          "stun:stun1.l.google.com:19302",
          "stun:stun2.l.google.com:19302",
          "stun:stun3.l.google.com:19302",
          "stun:stun4.l.google.com:19302",
        ]
    }],
    socketType: SocketType.UDP,
    timeout: 3000,
    retries: 3,
    retryTimeout: 1000,
};


export abstract class BasePeerConnection {
    protected initiator : boolean = false;
    readonly channelId: string;
    protected config: PeerConnectionConfig = defaultConfig;
    private queuedIceCandidates: Array<any> = [];
    public status : PEER_CONNECTION_STATUS = PEER_CONNECTION_STATUS.NONE;

  private peerConnection: RTCPeerConnection;
  protected channels: Map<string, RTCDataChannel> = new Map();
  protected primaryChannelLabel?: string;
    public connected: boolean = false;
    public connectedAt : number = -1;

    private remoteDescriptionSet: boolean = false;

    public ourHandshakeOptions?: any;
    public peerHandshakeOptions?: any;

    constructor(configOptions?: PeerConnectionConfig) {
      this.config = {
        ...defaultConfig,
        ...(configOptions || {})
      }
      // create unique channel id for players by ordering by index then joining
      this.channelId = configOptions.channelName;
    }

    private registerDataChannel(channel: RTCDataChannel) {
      const { label } = channel;
      if(!this.primaryChannelLabel) {
        this.primaryChannelLabel = label;
      }
      this.channels.set(label, channel);
      channel.binaryType = 'arraybuffer';
      channel.onopen = () => this.onDataChannelOpen(label);
      channel.onclose = () => this.onConnectionClose(label);
      channel.onmessage = (event) => this.onPeerMessage(label, event.data);
      // A channel the remote announced (`ondatachannel`) can already be open when it reaches us: its `open` event
      // fired before the handler above existed, and no second one comes. Treat it as opened now — otherwise its first
      // message arrives on a channel this connection never marked ready.
      if (channel.readyState === 'open') this.onDataChannelOpen(label);
    }


    abstract onDataChannelOpen(label: string) : void;
    abstract onConnectionClose(label: string) : void;
    abstract onPeerMessage(label: string, data: any) : void;
    abstract onIceCandidate(event: { candidate?: RTCIceCandidate }) : void;
    abstract onLocalDescription(event: { sdp: RTCLocalSessionDescriptionInit, isInitial?: boolean, requestOptions?: any }) : void;

    private applyQueuedIceCandidates() {
      for(let i = 0; i < this.queuedIceCandidates.length; i++) {
        const candidate = this.normalizeCandidate(this.queuedIceCandidates[i]);
        this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      }
      this.queuedIceCandidates.length = 0;
    }
    
    public async handleSDPSignal(sdp) {
      this.peerConnection.setRemoteDescription(new RTCSessionDescription(sdp)).then((desc) => {
        if(!this.peerConnection.remoteDescription) {
          return
        }
        if (this.peerConnection.remoteDescription.type === 'offer') {
          this.peerConnection.createAnswer().then(desc => {
              this.handleLocalDescription(desc)
          })
        }
        this.remoteDescriptionSet = true;
        this.applyQueuedIceCandidates();
      }).catch(err => {
        console.error('ClientSide: Error handling sdp signal...', err);
      });
    };

    public handleIceCandidateSignal(candidate) {
      if(this.remoteDescriptionSet) {
        this.peerConnection.addIceCandidate(new RTCIceCandidate(this.normalizeCandidate(candidate)));
      } else {
        this.queuedIceCandidates.push(candidate);
      }
    }

    private handleLocalDescription(desc) {
        this.peerConnection.setLocalDescription(desc).then(() => {
          this.onLocalDescription({ sdp: this.peerConnection.localDescription });
        }).catch(err => {
            console.error(err);
        });
      }

    private handleInitialLocalDescription(desc, requestOptions?: any) {
      this.peerConnection.setLocalDescription(desc).then(() => {
        // console.log('SEND UP PEER SIGNAL REQUESThandleInitialLocalDescription')
        this.onLocalDescription({ sdp: this.peerConnection.localDescription, isInitial: true, requestOptions })
      }).catch(err => {
        console.error(err);
      });
    }

  private logError(err) {
        throw new Error(err);
    }


    // reference this https://github.com/webrtc/samples/blob/gh-pages/src/content/datachannel/datatransfer/js/main.js
    public async requestConnection(requestOptions: any) {
      await ensureWRTCAdapterLoadFinished(); // Wait for adapter to load

      this.initiator = true;
      const config = {
        'iceServers': this.config.iceServers,
      } as RTCConfiguration;

      this.peerConnection = new RTCPeerConnection(config);
      this.peerConnection.addEventListener('icecandidate', this.onIceCandidate.bind(this));
    
      this.peerConnection.onnegotiationneeded = () => {
        this.peerConnection.createOffer().then((offer) => {
          this.handleInitialLocalDescription(offer, requestOptions)
        }).catch(err => {
          this.logError(err);
        });
      };

      const channel = this.peerConnection.createDataChannel(this.channelId, {
          ordered: this.config.socketType === SocketType.TCP
      });

      this.registerDataChannel(channel);
    }

    /**
     * used for incoming signal requests
     */
    public async acceptConnection() {
      await ensureWRTCAdapterLoadFinished(); // Wait for adapter to load
      this.initiator = false;

      this.status = PEER_CONNECTION_STATUS.WAITING_FOR_HANDSHAKE;

      this.peerConnection = new RTCPeerConnection({ iceServers: this.config['iceServers'] });
      this.peerConnection.addEventListener('icecandidate', this.onIceCandidate.bind(this));

      this.peerConnection.ondatachannel = event => {
        this.registerDataChannel(event.channel);
      }
    }

    public send(message: string | ArrayBuffer, channelLabel?: string) {
      const label = channelLabel ?? this.primaryChannelLabel;
      if(!label) {
        return;
      }
      const channel = this.channels.get(label);
      if(channel && channel.readyState === 'open') {
        channel.send(message as any);
      }
    }

    /**
     * Await every open data channel draining its buffered send queue
     * (`bufferedAmount` → 0), capped at `timeoutMs` so a stuck channel can never
     * hang the caller. Called before a DELIBERATE teardown so a final reliable
     * frame (PROTOCOLS.CLIENT_LEAVING) is transmitted before destroy() closes
     * the SCTP transport. If a transport does not report `bufferedAmount`, the
     * check reads as already-drained and this returns immediately (safe: no
     * worse than the pre-flush behavior).
     */
    public async flush(timeoutMs = 250): Promise<void> {
      const deadline = Date.now() + timeoutMs;
      const drained = () => {
        for (const channel of this.channels.values()) {
          if (channel.readyState === 'open' && (channel.bufferedAmount ?? 0) > 0) return false;
        }
        return true;
      };
      while (!drained() && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 10));
      }
    }

    public destroy() {
      this.channels.forEach((channel) => {
        try {
          channel.close();
        } catch (err) {
          console.error('Error closing data channel', err);
        }
      });
      this.channels.clear();
      this.peerConnection.close();
    }

    // wrtc may emit candidates with mid/sdpMid mismatches or string m-line indexes; normalize for stability.
    private normalizeCandidate(candidate: any): RTCIceCandidateInit {
      if (!candidate || typeof candidate !== 'object') {
        return candidate;
      }

      const normalized: any = { ...candidate };

      if (normalized.sdpMid == null && typeof normalized.mid === 'string') {
        normalized.sdpMid = normalized.mid;
      }

      if (typeof normalized.sdpMLineIndex === 'string') {
        const parsed = Number.parseInt(normalized.sdpMLineIndex, 10);
        normalized.sdpMLineIndex = Number.isNaN(parsed) ? undefined : parsed;
      }

      if (typeof normalized.sdpMLineIndex !== 'number') {
        normalized.sdpMLineIndex = 0;
      }

      return normalized;
    }
}